import type { GatherTierDef, InfraDef } from './types';

/**
 * Auto-gathering tiers. Rates are eggs/second added per purchase; the
 * valueBonus of your best tier multiplies auto-collected egg value (it
 * narrows the gap between idle gathering and active clicking).
 */
export const GATHER_TIERS: readonly GatherTierDef[] = [
  { id: 'basket', name: 'Basic Egg Basket', tier: 1, cost: 50, rate: 0.2, maxOwned: 2, valueBonus: 1, postWin: false,
    flavor: 'Your first auto-collector: a basket and good intentions. Buy it twice for double speed.' },
  { id: 'tray', name: 'Egg Tray', tier: 2, cost: 150, rate: 0.3, maxOwned: 1, valueBonus: 1, postWin: false,
    flavor: 'Thirty little cups. Very organized.' },
  { id: 'wicker', name: 'Wicker Basket', tier: 3, cost: 400, rate: 0.5, maxOwned: 2, valueBonus: 1, postWin: false,
    flavor: 'Handwoven. Stackable. Buy two.' },
  { id: 'apron', name: 'Gathering Apron', tier: 4, cost: 1_200, rate: 0.7, maxOwned: 1, valueBonus: 1, postWin: false,
    flavor: 'Big pockets. Bigger ambitions.' },
  { id: 'collector', name: 'Egg Collector', tier: 5, cost: 5_000, rate: 1.0, maxOwned: 1, valueBonus: 1.1, postWin: false,
    flavor: 'A part-time farmhand. Handles eggs gently, which bumps their sale value.' },
  { id: 'conveyor', name: 'Conveyor Belt', tier: 6, cost: 30_000, rate: 1.5, maxOwned: 1, valueBonus: 1.2, postWin: false,
    flavor: 'Eggs roll to the packing table while you sleep.' },
  { id: 'chute', name: 'Egg Chute', tier: 7, cost: 70_000, rate: 1.6, maxOwned: 1, valueBonus: 1.3, postWin: false,
    flavor: 'Gravity-fed slide into a single collection bin.' },
  { id: 'pneumatic', name: 'Pneumatic Gatherer', tier: 8, cost: 150_000, rate: 2.2, maxOwned: 1, valueBonus: 1.4, postWin: false,
    flavor: 'Air-powered tubes whisk eggs to the sorting line.' },
  { id: 'automated', name: 'Automated Gathering', tier: 9, cost: 250_000, rate: 10, maxOwned: 1, valueBonus: 1.5, postWin: false,
    flavor: 'A conveyor-fed sorter grabs eggs the moment they drop.' },
  { id: 'smartCoop', name: 'Smart Coop', tier: 10, cost: 500_000, rate: 15, maxOwned: 1, valueBonus: 1.6, postWin: true,
    flavor: 'The coop runs itself. Eggs roll straight from nest to basket.' },
  { id: 'robotic', name: 'Robotic Picker', tier: 11, cost: 1_000_000, rate: 20, maxOwned: 1, valueBonus: 1.7, postWin: true,
    flavor: 'Drone arms pluck eggs mid-flight.' },
  { id: 'aiLoop', name: 'AI Feed Loop', tier: 12, cost: 10_000_000, rate: 40, maxOwned: 1, valueBonus: 1.85, postWin: true,
    flavor: 'A closed-loop gather cycle that learns where the eggs will be.' },
  { id: 'zeroOps', name: 'Zero-Ops Coop', tier: 13, cost: 100_000_000, rate: 80, maxOwned: 1, valueBonus: 2, postWin: true,
    flavor: 'The ceiling. Clicks still compound on top.' },
];

export const GATHER_BY_ID: ReadonlyMap<string, GatherTierDef> = new Map(GATHER_TIERS.map((g) => [g.id, g]));

/** Infrastructure: stackable buildings with a per-unit effect and a hard cap. */
export const INFRA: readonly InfraDef[] = [
  { id: 'scratchFeeder', name: 'Scratch Feeder', baseCost: 3_000, costGrowth: 1.5, maxOwned: 5,
    perUnit: [{ stat: 'valueFlat', op: 'add', value: 0.25 }], requires: [{ kind: 'hens', count: 3 }],
    flavor: 'Scatter feed keeps hens busy and eggs a little richer.' },
  { id: 'waterer', name: 'Waterer', baseCost: 20_000, costGrowth: 1.5, maxOwned: 5,
    perUnit: [{ stat: 'valueFlat', op: 'add', value: 0.5 }], requires: [{ kind: 'hens', count: 6 }],
    flavor: 'Clean water, better eggs. Groundbreaking stuff.' },
  { id: 'eggInspector', name: 'Egg Inspector', baseCost: 13_000, costGrowth: 1.15, maxOwned: 3,
    perUnit: [{ stat: 'salvageWindow', op: 'add', value: 30 }], requires: [{ kind: 'hens', count: 8 }],
    flavor: 'Catches bad eggs before they stink up the coop.' },
  { id: 'warmingLamp', name: 'Warming Lamp', baseCost: 200_000, costGrowth: 1.6, maxOwned: 3,
    perUnit: [{ stat: 'value', op: 'mul', value: 1.5 }], requires: [{ kind: 'hens', count: 12 }],
    flavor: 'Toasty hens are productive hens. Capped at three before the fire marshal visits.' },
];

export const INFRA_BY_ID: ReadonlyMap<string, InfraDef> = new Map(INFRA.map((i) => [i.id, i]));
